import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import HrefLangAlternates from "@/components/HrefLangAlternates";
import { Clock, ArrowRight, Tag, Search } from "lucide-react";

/**
 * BlogIndexPage, public blog landing.
 * SEO: title, description, OpenGraph + JSON-LD Blog schema.
 */
export default function BlogIndexPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const { lang } = useApp();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blog?language=${lang}`);
        setPosts(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  const allTags = useMemo(() => {
    const s = new Set();
    posts.forEach((p) => (p.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [posts]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return posts
      .filter((p) => !tag || (p.tags || []).includes(tag))
      .filter((p) => !needle || p.title.toLowerCase().includes(needle) || p.excerpt.toLowerCase().includes(needle));
  }, [posts, q, tag]);

  const canonicalUrl = `${window.location.origin}/blog`;

  // JSON-LD: Blog schema
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Kiosk Configurator, Inzichten & gidsen",
    description: "Praktische gidsen, ROI-berekeningen en best-practices over kiosken, PMS-integraties en self-service.",
    url: canonicalUrl,
    inLanguage: lang || "nl",
    blogPost: posts.slice(0, 20).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.excerpt,
      url: `${window.location.origin}/blog/${p.slug}`,
      datePublished: p.published_at,
      dateModified: p.updated_at || p.published_at,
      author: { "@type": "Organization", name: p.author_name || "Kiosk Configurator" },
      image: p.hero_image ? `${window.location.origin}${p.hero_image}` : undefined,
    })),
  };

  return (
    <div data-testid="blog-index" className="min-h-screen pb-16">
      <Helmet>
        <html lang={lang || "nl"} />
        <title>Blog & gidsen, Kiosk Configurator | Self-service insights</title>
        <meta name="description" content="Praktische gidsen, ROI-berekeningen en best-practices over kiosken, PMS-integraties, polsbandjes-toegang, hotel- en campingcheckin." />
        <link rel="canonical" href={canonicalUrl} />
        {/* OpenGraph */}
        <meta property="og:title" content="Blog & gidsen, Kiosk Configurator" />
        <meta property="og:description" content="Praktische gidsen over kiosken, PMS-integraties en self-service." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog & gidsen, Kiosk Configurator" />
        <meta name="twitter:description" content="Praktische gidsen over kiosken en self-service." />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <HrefLangAlternates path="/blog" />

      {/* Hero */}
      <section className="container-x pt-12 pb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D97742] mb-2">Inzichten & gidsen</div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-white leading-tight max-w-3xl">
          Praktische gidsen over kiosken, integraties & self-service
        </h1>
        <p className="text-base text-[#A8B3AD] mt-4 max-w-2xl">
          Hoe een zwembad polsbandjes regelt zonder receptie. Wat een onbemande camping check-in kost. Wat self-order restaurants écht oplevert. Geen marketing-praat, alleen praktische cijfers en setups.
        </p>
      </section>

      {/* Filters */}
      <section className="container-x pb-8">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8B3AD]" />
            <input
              data-testid="blog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Zoek artikel…"
              className="w-full pl-9 pr-3 py-2 rounded bg-[#0F1816] border border-[#1F3A2E] text-sm text-white placeholder-[#5d6a64] focus:outline-none focus:border-[#D97742]"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Chip active={!tag} onClick={() => setTag("")}>Alle</Chip>
            {allTags.slice(0, 10).map((t) => (
              <Chip key={t} active={tag === t} onClick={() => setTag(tag === t ? "" : t)}>{t}</Chip>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container-x">
        {loading && <div className="text-center py-20 text-[#A8B3AD]">Laden…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 text-[#A8B3AD]">Geen artikelen gevonden.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <BlogCard key={p.id} post={p} />
          ))}
        </div>
      </section>
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${active ? "bg-[#D97742] border-[#D97742] text-white" : "border-[#1F3A2E] text-[#A8B3AD] hover:border-[#D97742]/50"}`}
    >
      {children}
    </button>
  );
}

function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="card-surface overflow-hidden flex flex-col group hover:translate-y-[-2px] hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all"
    >
      <div className="h-44 bg-[#0F1816] overflow-hidden relative">
        {post.hero_image && (
          <img src={post.hero_image} alt={post.title} className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        )}
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-medium bg-black/60 backdrop-blur text-white flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" /> {post.reading_time_minutes} min
        </span>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-base font-semibold text-white leading-snug group-hover:text-[#D97742] transition">{post.title}</h3>
        <p className="text-xs text-[#A8B3AD] mt-2 line-clamp-3 flex-1">{post.excerpt}</p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {(post.tags || []).slice(0, 2).map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#1F3A2E] text-[#7FD8B8]">#{t}</span>
            ))}
          </div>
          <span className="text-[11px] text-[#D97742] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
            Lees verder <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}
